/**
 * String redaction for browser telemetry (RUM + browser logs), applied by the SDK `beforeSend` hooks.
 *
 * Query values and fragments can hold anything a user typed (search boxes sync to the URL) or a
 * credential (presigned S3 signatures), so they never leave the browser. Scheme, host, path and
 * query keys are kept, so events stay groupable by route and endpoint.
 */
const REDACTED = 'redacted';

// Extension schemes are included so their stack frames are treated like any other script URL.
export const URL_PATTERN =
  /\b(?:https?|wss?|chrome-extension|moz-extension|safari-web-extension):\/\/[^\s'"`<>()]+/g;
// Bounded quantifiers (RFC length limits) keep this linear on long inputs such as stacks; it runs on
// every telemetry event on the main thread.
const EMAIL_PATTERN = /[\w.%+-]{1,64}@(?:[a-z\d-]{1,63}\.){1,8}[a-z]{2,24}/gi;
// US-formatted numbers only: separators are required so numeric ids and timestamps survive.
const PHONE_PATTERN = /(?:\+?1[\s.-]?)?(?:\(\d{3}\)\s?|\b\d{3}[\s.-])\d{3}[\s.-]\d{4}\b/g;

const splitOnce = (value: string, separator: string): [string, string | undefined] => {
  const index = value.indexOf(separator);
  return index === -1 ? [value, undefined] : [value.slice(0, index), value.slice(index + 1)];
};

const redactQuery = (query: string) =>
  query
    .split('&')
    .map((pair) => {
      const [key, value] = splitOnce(pair, '=');
      if (value === undefined) {
        // A bare token (`?jane@example.com`) is a value, not a key.
        return pair ? REDACTED : pair;
      }
      return value ? `${key}=${REDACTED}` : pair;
    })
    .join('&');

export const redactUrl = (url: string): string => {
  const [withoutFragment, fragment] = splitOnce(url, '#');
  const [base, query] = splitOnce(withoutFragment, '?');
  const redactedQuery = query === undefined ? '' : `?${redactQuery(query)}`;
  const redactedFragment = fragment ? `#${REDACTED}` : '';
  return `${base}${redactedQuery}${redactedFragment}`;
};

/** Redacts URLs, emails and phone numbers embedded in free text (messages, stacks, action names). */
export const redactText = (text: string): string =>
  text
    .replaceAll(URL_PATTERN, (url) => redactUrl(url))
    .replaceAll(EMAIL_PATTERN, '[email]')
    .replaceAll(PHONE_PATTERN, '[phone]');
